import {
  NEWS_API_ORIGIN,
  NEWS_ARTICLES_PATH,
  NEWS_PAGE_SIZE,
  NEWS_TIMEOUT_MS,
} from '../config/news';
import type { NewsArticle, NewsCoverImage, NewsPage, NewsPublisher } from '../types';

/**
 * Fil d'articles d'alternjob.com — lecture paginée de son API REST publique.
 *
 * Même source que l'encart de l'accueil (`content.ts`), mais un contrat différent : ici on demande
 * une PAGE, là-bas on prenait la liste entière. Les deux coexistent sans se gêner ; les fusionner
 * ferait dépendre l'encart d'une pagination dont il n'a pas l'usage.
 *
 * `limit` EST TOUJOURS TRANSMIS, dès la première requête. Sans lui l'API répond tout d'un bloc,
 * sans enveloppe `pageInfo` : la pagination serait alors désactivée en silence, ce qui est pire
 * qu'une erreur — le fil marcherait, simplement il ne chargerait jamais rien de plus.
 *
 * TOUT CE QUI ARRIVE EST SUSPECT. Le CMS derrière cette API laisse des champs vides, et un type
 * TypeScript ne se vérifie pas à l'exécution. Chaque valeur est donc reprise une à une, avec son
 * type contrôlé ; ce qui ne va pas devient `undefined` et l'affichage dégrade. Un `as NewsArticle`
 * sur la réponse brute aurait la même signature et aucune de ces garanties.
 */
function texte(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v : undefined;
}

function nombre(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : undefined;
}

function objet(v: unknown): Record<string, unknown> | undefined {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined;
}

/**
 * L'éditeur est un objet PLAT : `logoUrl`, `logoWidth`, `logoHeight` sont au même niveau que `name`,
 * il n'y a pas de sous-objet `logo`. Une lecture en `publisher.logo.url` rendrait `undefined` en
 * silence et la carte s'afficherait sans logo, sans que rien ne signale l'erreur.
 */
export function parsePublisher(brut: unknown): NewsPublisher | undefined {
  const p = objet(brut);
  if (!p) return undefined;
  const publisher: NewsPublisher = {
    id: texte(p.id),
    name: texte(p.name),
    logoUrl: texte(p.logoUrl),
    logoWidth: nombre(p.logoWidth),
    logoHeight: nombre(p.logoHeight),
    verified: p.verified === true,
  };
  // Un éditeur sans nom NI logo n'a rien à rendre : autant que la carte sache qu'il n'y en a pas,
  // plutôt qu'elle réserve une ligne vide.
  return publisher.name || publisher.logoUrl ? publisher : undefined;
}

/**
 * `coverImage` est la source de vérité de l'illustration. Le champ `image` de l'API en est un
 * doublon et `cover` un ancien dégradé CSS : ni l'un ni l'autre n'est lu, et les ignorer ici plutôt
 * qu'au rendu évite qu'ils reviennent par une modification distraite.
 *
 * Sans `url`, pas d'image : les dimensions seules ne servent à rien.
 */
export function parseCoverImage(brut: unknown): NewsCoverImage | undefined {
  const c = objet(brut);
  const url = c ? texte(c.url) : undefined;
  if (!c || !url) return undefined;
  return { url, width: nombre(c.width), height: nombre(c.height), alt: texte(c.alt) };
}

export function parseArticle(brut: unknown): NewsArticle | null {
  const a = objet(brut);
  const id = a ? texte(a.id) : undefined;
  // SANS ID, ON JETTE. C'est la clé React de la liste et la clé du favori : un article sans id
  // produirait un doublon de clé et un signet impossible à retirer. Le laisser passer pour
  // « afficher quand même » échangerait un article manquant contre un bug durable en base.
  if (!a || !id) return null;

  return {
    id,
    slug: texte(a.slug),
    title: texte(a.title),
    excerpt: texte(a.excerpt),
    category: texte(a.category),
    date: texte(a.date),
    readingMinutes: nombre(a.readingMinutes),
    publisher: parsePublisher(a.publisher),
    coverImage: parseCoverImage(a.coverImage),
  };
}

/**
 * Normalise l'enveloppe paginée.
 *
 * `nextCursor` vaut `string | null` — explicitement `null` en fin de fil, jamais `undefined`. On le
 * ramène malgré tout à `null` s'il est absent ou d'un autre type : l'appelant n'a qu'un seul cas
 * d'arrêt à traiter, et une réponse malformée s'arrête au lieu de continuer avec `"undefined"` en
 * paramètre d'URL.
 */
export function parsePage(brut: unknown): NewsPage {
  const enveloppe = objet(brut);
  const liste = enveloppe && Array.isArray(enveloppe.articles) ? enveloppe.articles : [];
  const info = enveloppe ? objet(enveloppe.pageInfo) : undefined;

  return {
    articles: liste.map(parseArticle).filter((a): a is NewsArticle => a !== null),
    hasNextPage: info?.hasNextPage === true,
    nextCursor: info && typeof info.nextCursor === 'string' && info.nextCursor !== ''
      ? info.nextCursor
      : null,
  };
}

/**
 * Construit l'URL d'une page.
 *
 * LE CURSEUR EST OPAQUE : il est transmis tel quel, seulement encodé pour l'URL. On ne le décode
 * pas, on ne l'inspecte pas et on n'en fabrique jamais un — sa forme (« MjAyNi0wOC0wOHxhcnRpYy… »)
 * est un détail du serveur, qui peut en changer sans prévenir.
 */
export function buildPageUrl(cursor?: string | null, limit: number = NEWS_PAGE_SIZE): string {
  const base = `${NEWS_API_ORIGIN}${NEWS_ARTICLES_PATH}?limit=${encodeURIComponent(String(limit))}`;
  return cursor ? `${base}&cursor=${encodeURIComponent(cursor)}` : base;
}

export async function fetchArticlesPage(
  cursor?: string | null,
  limit: number = NEWS_PAGE_SIZE,
): Promise<NewsPage> {
  const controleur = new AbortController();
  const minuteur = setTimeout(() => controleur.abort(), NEWS_TIMEOUT_MS);
  try {
    const reponse = await fetch(buildPageUrl(cursor, limit), { signal: controleur.signal });
    if (!reponse.ok) throw new Error(`news_http_${reponse.status}`);
    return parsePage(await reponse.json());
  } finally {
    clearTimeout(minuteur);
  }
}
